import React from 'react';
import { Box, Typography } from '@mui/material';
import CircularIcon from 'components/icon/circular-icon';
import { spacing, typography } from 'design-system';

/**
 * CircularIconLabel Props
 */
export interface CircularIconLabelProps {
  /** Icon element */
  icon: React.ReactNode;
  
  /** Label text (e.g., "3 Beds") */
  label: string;
  
  /** Whether to blur the label (icon remains visible) */
  blur?: boolean;
  
  /** Click handler (optional) */
  onClick?: () => void;
  
  /** Additional CSS class */
  className?: string;
}

/**
 * CircularIconLabel Component
 * 
 * Displays a circular icon with a label below (mobile variant).
 * 
 * Design Specs (per Figma node-id=1249:1166):
 * - Circular icon container (44px)
 * - Label below icon (14px Medium)
 * - 4px gap between icon and label
 * - Vertical layout
 * 
 * @example
 * ```tsx
 * <CircularIconLabel icon={<BedIcon />} label="3 Beds" />
 * ```
 */
export default function CircularIconLabel({
  icon,
  label,
  blur,
  onClick,
  className,
}: CircularIconLabelProps) {
  return (
    <Box
      className={className}
      onClick={onClick}
      sx={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: spacing.sm, // 4px per Figma mobile specs
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick ? {
          opacity: 0.8,
        } : {},
      }}
    >
      <CircularIcon icon={icon} size="md" />
      <Typography
        variant="body2"
        sx={{
          fontSize: typography.fontSize.md, // 14px per Figma
          fontWeight: typography.fontWeight.regular, // 400 per Figma
          lineHeight: typography.lineHeight.base,
          textAlign: 'center',
          ...(blur && {
            filter: 'blur(6px)',
            userSelect: 'none',
          }),
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}
